import React, {useState} from "react";
import {useSelector} from "react-redux";
import LoadingComponent from "../../components/LoadingComponent";
import {useTranslation} from "react-i18next";
import {Employee, EmploymentStatus} from "../../app/interfaces";
import {useNavigate} from "react-router-dom";
import useFetchEmployees from "../../hooks/useFetchEmployees";
import {
    registeredStatuses
} from "../../utils/constants";
import {capitalize, fullNameForUser, isValidString} from "../../utils";
import defaultAvatarImage from '../../assets/images/avatar.svg';
import {
    TableDataCell,
    TableRow,
    TablePrimaryText,
    TableSecondaryText,
    TableHeaderCell
} from "../../components/Table/styles";
import {
    Table,
    TableBody,
    TableHeader,
    TableNavigation
} from "../../components/Table";
import {
    DataTableLayoutFilter,
    DataTableLayoutFooter,
    DataTableLayoutHeader,
    DataTableLayoutTitle
} from "../../layouts/DataTableLayout/styles";

import DataTableLayout, {EmptyDataTableLayout} from "../../layouts/DataTableLayout/DataTableLayout";
import {EmployeeInfo} from "./styles";
import {countryNameByCode} from "../../utils/countryCodes";
import {currencySymbolByCode} from "../../utils/currencies";
import {Filter, FlexColumn, FlexRow, SearchBox, StatusPill} from "../../components";
import {allEmployeesFetchError, allEmployeesFetchState, selectedEmployeesPaginated} from "../../app/employee/selectors";

interface ActiveEmployeesProps {
    itemsPerPage: number;
}

export const ActiveEmployees = ({itemsPerPage}: ActiveEmployeesProps) => {

    useFetchEmployees()

    const [currentPage, setCurrentPage] = useState<number>(1)
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [employmentStatuses, setEmploymentStatuses] = useState<EmploymentStatus[]>([])

    const employees = useSelector(
        selectedEmployeesPaginated(
            currentPage,
            itemsPerPage,
            (employmentStatuses.length ? employmentStatuses : registeredStatuses),
            searchTerm
        )
    );

    const fetchState = useSelector(allEmployeesFetchState)
    const error = useSelector(allEmployeesFetchError)
    const navigate = useNavigate();
    const {t} = useTranslation();

    const dataTableTitle = t('employees')

    if (fetchState === 'loading' || fetchState === 'idle') {
        return <EmptyDataTableLayout title={dataTableTitle}>
            <LoadingComponent/>
        </EmptyDataTableLayout>
    }

    if (error) {
        console.error(error)
        return <EmptyDataTableLayout title={dataTableTitle}>
            {error}
        </EmptyDataTableLayout>
    }

    if (!employees.items.length) {
        return <EmptyDataTableLayout
            title={dataTableTitle}
            filters={<DataTableFilters setSearchTerm={setSearchTerm} filters={employmentStatuses} setFilters={setEmploymentStatuses}/>}
        >
            {t('no_employees_found') as string}
        </EmptyDataTableLayout>
    }

    return <DataTableLayout>
        <DataTableLayoutHeader>
            <DataTableLayoutTitle>
                {dataTableTitle}
            </DataTableLayoutTitle>
            <DataTableFilters setSearchTerm={setSearchTerm} filters={employmentStatuses} setFilters={setEmploymentStatuses}/>
        </DataTableLayoutHeader>
        <Table>
            <TableHeader>
                <TableHeaderCell>{t('full_name')}</TableHeaderCell>
                <TableHeaderCell className="lg-hide">{t('position')}</TableHeaderCell>
                <TableHeaderCell style={{textAlign: 'center'}}>{t('salary')}</TableHeaderCell>
                <TableHeaderCell style={{textAlign: 'center'}}>{t('status')}</TableHeaderCell>
            </TableHeader>
            <TableBody>
                {employees.items.map((e, i) => EmployeeRow(e, i, navigate, t))}
            </TableBody>
        </Table>
        <DataTableLayoutFooter>
            <TableNavigation
                pageLimit={itemsPerPage}
                summary={employees.summary}
                setPage={setCurrentPage}
            />
        </DataTableLayoutFooter>
    </DataTableLayout>
}

interface DataTableFiltersProps {
    setSearchTerm: Function
    filters: string[]
    setFilters: Function
}
const DataTableFilters = ({setSearchTerm, filters, setFilters }: DataTableFiltersProps) => {
    return <DataTableLayoutFilter>
        <SearchBox
            placeholder="Search employee"
            setSearchTerm={setSearchTerm}
        />
        <Filter
            filters={registeredStatuses}
            selectedFilters={filters}
            setSelectedFilters={setFilters}
        />
    </DataTableLayoutFilter>
}

const EmployeeRow = (employee: Employee, i: number, navigate: Function, t: Function) => {
    return <TableRow
        key={i}
        clickable={true}
        onClick={() => navigate(`/employee/${employee._id}`)}
    >
        <TableDataCell>
            <EmployeeInfo>
                <FlexRow align="center">
                    {/*@ts-ignore */}
                    <img alt="avatar" src={isValidString(employee?.avatar) ? `${process.env.REACT_APP_API_URL}/${employee.avatar}` : defaultAvatarImage}/>
                    <FlexColumn justify="space-between">
                        <TablePrimaryText>{capitalize(fullNameForUser(employee))}</TablePrimaryText>
                        <TableSecondaryText>
                            {`${t('employee')} - ${t(countryNameByCode(employee.tax_residence))}`}
                        </TableSecondaryText>
                    </FlexColumn>
                </FlexRow>
            </EmployeeInfo>
        </TableDataCell>
        <TableDataCell className="lg-hide">
            <TableSecondaryText> {employee.activity} </TableSecondaryText>
        </TableDataCell>
        <TableDataCell style={{textAlign: 'center'}}>
            <TablePrimaryText>
                {`${employee.salary.amount}${currencySymbolByCode(employee.salary.currency)}`}
            </TablePrimaryText>
        </TableDataCell>
        <TableDataCell>
            <FlexColumn align="center" justify="center" style={{height: '100%'}}>
                <StatusPill
                    status={employee.status}
                >
                    {t(employee.status)}
                </StatusPill>
            </FlexColumn>
        </TableDataCell>
    </TableRow>
}

export default ActiveEmployees;
